import React, { useEffect, useRef, useState } from 'react';
import { useProgress } from '@react-three/drei';
import { useWorldStore } from '../state/worldStore';
import { SLICE_DISTRICT, IS_GUEST, API_BASE } from '../config';
import { audioEngine } from '../services/audioEngine';

// Loader — the loading→enter gate, styled as the full "HypeTown" landing page
// (design 1B): a scrollable brand page with hero, an auto-scrolling "scenes"
// marquee, the venues, why-it's-different cards, and a how-it-works close.
// The primary CTA is the ONE enter gesture: it reads "Loading…" (disabled +
// spinner) until assets settle, then flips to "▶ Enter HypeTown". The click is
// the user gesture that lets audio start; it also kicks the cinematic fly-in and
// fades this whole page into it. Guests get inline login + "enter as guest".
const IS_TOUCH = typeof window !== 'undefined' && (('ontouchstart' in window) || navigator.maxTouchPoints > 0);

// How long the loading manager has to stay quiet before we believe it. See the
// long note on the ready effect — the boot is a CHAIN of loads, and the gaps
// between its links look identical to being finished.
const QUIET_MS = 800;
// Escape hatch only. Never the normal path — if this fires, something is wrong.
const SAFETY_MS = 30000;

const TIPS = [
  'Live AI artists · their own town',
  'A studio · a cinema · a concert hall',
  'Walk in · look around · stay a while',
  'The radio is always on',
];

// ONE row does both jobs. This used to be two: a stills marquee ("scenes from
// the town") and a static grid of described venue cards below it — the same
// five places, twice. The marquee now carries the described cards themselves,
// and the street-level stills that had no card (downtown, billboards, the
// alley) were given one so none of that imagery was lost. Rendered twice for
// the seamless -50% loop.
const VENUES = [
  { emoji: '🎙', name: 'Studio', line: 'See where the songs are made. Step into the booth and talk to the artist live — voice or video. They remember you.', img: '/images/world/studio.jpg' },
  { emoji: '💿', name: 'Record Shop', line: 'Flip through the featured releases and drop the needle. Play anything on the spot.', img: '/images/world/shop.jpg' },
  { emoji: '🎬', name: 'Cinema', line: 'Big screens running music videos back to back. Pull up a seat.', img: '/images/world/cinema.jpg' },
  { emoji: '🎤', name: 'Stage', line: 'Front-row to a performance on the virtual stage — lights, crowd, and all.', img: '/images/world/concerts.jpg' },
  { emoji: '🎧', name: 'Listening Lounge', line: 'Always on. Every district streams its own station with live hosts between the tracks.', img: '/images/world/lounge.jpg' },
  { emoji: '🌆', name: 'The Streets', line: 'Walk the city between the venues — traffic, neon, and artists out living their day.', img: '/images/world/downtown.jpg' },
  { emoji: '📺', name: 'Billboards', line: 'The screens above the block run artist takeovers and new video drops. Look up.', img: '/images/world/billboards.jpg' },
  { emoji: '🧱', name: 'The Alley', line: 'The shortcut with more character than the street — brick, fire escapes, layered posters.', img: '/images/world/shady.jpg' },
];

const VALUES = [
  { title: 'The artists are alive.', body: 'Persistent lives — they perform, post, and remember you between visits.' },
  { title: 'Talk back.', body: 'Real-time voice and video conversations, not a chatbot box.' },
  { title: 'Free to roam.', body: 'Enter as a guest; sign up to go deeper.' },
];

const STEPS = [
  { n: '01', title: 'Enter', body: 'One tap, no install.' },
  { n: '02', title: 'Explore', body: 'Walk the streets, step into any venue.' },
  { n: '03', title: 'Connect', body: 'Talk to the artists, catch a show, stay a while.' },
];

export default function Loader() {
  const { active, progress } = useProgress();
  const entered = useWorldStore((s) => s.entered);
  const setEntered = useWorldStore((s) => s.setEntered);
  const [ready, setReady] = useState(false);
  const [graced, setGraced] = useState(false);
  const [gone, setGone] = useState(false);
  const [tip, setTip] = useState(0);
  // The highest percentage shown so far — see `pct` below.
  const peak = useRef(3);
  // Login (guests only): email/password → /api/auth/login → save JWT → reload
  // (AUTH_TOKEN is read at module load, so a reload is what promotes them to a
  // member). `mode` toggles the inline login form.
  const [mode, setMode] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authBusy, setAuthBusy] = useState(false);
  const [authErr, setAuthErr] = useState('');

  // Buffer the station now (during loading), so the Enter click plays it instantly
  // and music is already going as the fly-in begins.
  useEffect(() => { audioEngine.prime(); }, []);

  // Grace so the brand moment reads AND loads have a beat to kick off before we
  // treat "!active" as done (a cached boot streams nothing through the manager).
  useEffect(() => { const t = setTimeout(() => setGraced(true), 900); return () => clearTimeout(t); }, []);

  // READY MEANS "QUIET FOR A WHILE", NOT "QUIET RIGHT NOW".
  //
  // useProgress().active follows THREE.DefaultLoadingManager, which reports idle
  // whenever every item REGISTERED SO FAR has finished. But the boot is a CHAIN,
  // not one batch: the city GLB downloads and parses, and only THEN registers its
  // KTX2 textures; a resident's mesh lands before its animation clips are asked
  // for. Every handoff is a moment with nothing in flight and `active` false.
  //
  // `setReady(true)` is one-way, so a single such gap any time after the 900ms
  // grace latched the gate open mid-load. The visitor then entered a city that
  // was still arriving — which is what "it tries to load everything while you are
  // in game" is.
  //
  // ON A REFRESH THIS WAS INVISIBLE, and that is the tell: everything comes from
  // the HTTP cache, the whole chain finishes inside the grace window, and the
  // world really is done when the gate opens. A bug that only exists on a first
  // visit is a bug about load TIMING, which is why the fix is here and not in
  // anything the first load happens to be drawing.
  //
  // Waiting out a quiet PERIOD closes it: any load that starts inside the window
  // cancels the pending timer through this effect's cleanup, so the gate opens
  // only once the chain has genuinely stopped.
  useEffect(() => {
    if (!graced || active) return undefined;
    const t = setTimeout(() => setReady(true), QUIET_MS);
    return () => clearTimeout(t);
  }, [graced, active]);

  // Safety: enable the gate even if load-tracking never settles, so no one is
  // stuck. AN ESCAPE HATCH, NOT A SCHEDULE — when it fires the world is still
  // loading and entering will be rough.
  //
  // It was 12s, and the cold-boot payload routinely exceeded that: the city and
  // skyline (~5MB) were sharing the connection with traffic (~7MB of car models),
  // the security guard (3.4MB) and a rigged resident with its clips (~19MB). So
  // TIMING OUT WAS THE NORMAL COLD-START PATH, and the 12s cap was doing the job
  // the ready check was supposed to do. City life is now deferred until you enter
  // (PlanetScene), which leaves the critical path at roughly the city itself.
  useEffect(() => { const t = setTimeout(() => setReady(true), SAFETY_MS); return () => clearTimeout(t); }, []);
  // Rotate the teaser tips.
  useEffect(() => { const i = setInterval(() => setTip((t) => (t + 1) % TIPS.length), 3200); return () => clearInterval(i); }, []);
  // Fade out after entering, then unmount.
  useEffect(() => { if (!entered) return undefined; const t = setTimeout(() => setGone(true), 850); return () => clearTimeout(t); }, [entered]);

  if (gone) return null;

  // ONLY EVER FORWARD. useProgress is loaded/total over three's loading
  // manager, and the boot is a CHAIN of batches (city → its textures → the
  // residents by the spawn, two at a time): every new batch grows `total`, so
  // the raw number hit 100 and fell back several times per load — which reads
  // as the page starting over. The shown number never goes down, and it holds
  // at 99 until the gate is genuinely ready (the quiet window above), so 100
  // means "you can go in" and nothing else.
  const raw = Math.max(3, Math.min(100, Math.round(progress || 0)));
  peak.current = Math.max(peak.current, ready ? 100 : Math.min(99, raw));
  const pct = peak.current;
  const hint = IS_TOUCH ? 'Drag to look · joystick to move' : 'Drag to look around · scroll to zoom';

  const onEnter = () => {
    if (!ready) return;
    // THE CLICK IS THE GESTURE, BUT NOT THE CUE.
    //
    // Khari, 2026-08-07: "halt the music at the entrance of the world? It
    // currently starts when you are in the selection screen... The radio should
    // instead start with the fly-in."
    //
    // This used to call setMusicOn(true) right here, so the station came up
    // under the character picker — the same complaint as 2026-08-03, which was
    // half-fixed then by holding `entered` down for that screen while the MUSIC
    // still started on this click.
    //
    // The gesture is still spent, because it has to be: browsers only allow
    // audio to begin inside a user event, and starting the station from the
    // fly-in would otherwise be refused on iOS in silence. `unlock()` blesses
    // the element without making a sound; the station itself starts with
    // `entered`, which CharacterSelect holds down until you actually leave for
    // the city ("NOW the city may start: fly-in, station, the lot").
    audioEngine.unlock().catch(() => {});
    setEntered(true);
  };

  const doLogin = async (e) => {
    e?.preventDefault();
    if (!email || !password) { setAuthErr('Enter your email and password.'); return; }
    setAuthBusy(true); setAuthErr('');
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.token) {
        setAuthErr(data.requiresVerification ? 'Please verify your email first.' : (data.error || 'Login failed.'));
        setAuthBusy(false);
        return;
      }
      try { localStorage.setItem('world:authToken', data.token); } catch { /* private mode */ }
      // Reload so config re-reads the token; the gate returns as a member.
      window.location.reload();
    } catch {
      setAuthErr('Network error — try again.'); setAuthBusy(false);
    }
  };

  // The loading-aware enter button (shared by hero + closing CTA).
  const enterButton = (label, variant = 'primary') => (
    <button
      className={`htl-btn htl-btn--${variant}${ready ? '' : ' is-loading'}`}
      onClick={onEnter}
      disabled={!ready}
    >
      {ready ? label : (
        <span className="htl-loading">
          <span className="htl-spinner" /> Loading… {pct}%
        </span>
      )}
    </button>
  );

  return (
    <div
      className="htl-root"
      style={{ opacity: entered ? 0 : 1, pointerEvents: entered ? 'none' : 'auto' }}
    >
      {/* slim brand bar */}
      <header className="htl-nav">
        <img className="htl-nav-logo" src="/logo.png" alt="HypeTunes" />
        <button className="htl-nav-signin" onClick={() => { setMode('login'); }}>Sign in</button>
      </header>

      {/* ── HERO ── */}
      <section className="htl-hero">
        <video
          className="htl-hero-video"
          src="/videos/world_announcement.mp4"
          autoPlay loop muted playsInline preload="metadata"
          aria-hidden="true"
        />
        <div className="htl-hero-scrim" aria-hidden="true" />
        <div className="htl-hero-inner">
          <span className="htl-badge"><span className="htl-badge-dot" />HYPETOWN · LIVE NOW</span>
          <h1 className="htl-title">Step inside the music.</h1>
          <p className="htl-sub">
            HypeTown is a living 3D city where AI music artists actually live — recording in the
            studio, playing the stage, spinning records in the lounge. You don't watch from the
            outside. You walk in.
          </p>

          {/* CTA — the one enter gesture. "Sign in" (nav) opens the login form
              in the hero for anyone; guests also get it inline + guest-entry. */}
          {mode === 'login' ? (
            <form className="htl-login" onSubmit={doLogin}>
              <input className="htl-input" type="email" placeholder="Email" value={email} autoComplete="email" onChange={(e) => setEmail(e.target.value)} />
              <input className="htl-input" type="password" placeholder="Password" value={password} autoComplete="current-password" onChange={(e) => setPassword(e.target.value)} />
              {authErr && <div className="htl-autherr">{authErr}</div>}
              <button className="htl-btn htl-btn--primary" type="submit" disabled={authBusy}>{authBusy ? 'Logging in…' : 'Log in'}</button>
              <button className="htl-linkbtn" type="button" onClick={() => { setMode(null); setAuthErr(''); }}>← Back</button>
            </form>
          ) : !IS_GUEST ? (
            <div className="htl-cta">
              {enterButton(`🚪 Enter ${SLICE_DISTRICT.name}`)}
            </div>
          ) : (
            <div className="htl-cta">
              {enterButton(`🚪 Enter ${SLICE_DISTRICT.name} — free`, 'ghost')}
              <button className="htl-btn htl-btn--primary" onClick={() => setMode('login')}>Log in</button>
            </div>
          )}

          <div className="htl-fineprint">No download. Runs in your browser, phone or desktop.</div>
          <div className="htl-hint">{ready ? hint : TIPS[tip]}</div>
        </div>
      </section>

      {/* ── What it is ── */}
      <section className="htl-intro">
        <p>
          HypeTunes built a town and handed it to the artists. They keep their own hours — making
          records, doing shows, talking to fans. Step off the street into any venue and it's
          happening live.
        </p>
      </section>

      {/* ── Venues marquee — the town's places, shown and described in one row.
             The list renders twice so the -50% loop is seamless; the second
             copy is aria-hidden so it isn't announced twice. ── */}
      <section className="htl-venues" id="htl-venues">
        <div className="htl-section-label">The venues</div>
        <div className="htl-marquee-viewport">
          <div className="htl-marquee-track">
            {[...VENUES, ...VENUES].map((v, i) => (
              <div className="htl-venue" key={i} aria-hidden={i >= VENUES.length ? 'true' : undefined}>
                <div className="htl-venue-media"><img src={v.img} alt={v.name} loading="lazy" /></div>
                <div className="htl-venue-body">
                  <div className="htl-venue-emoji">{v.emoji}</div>
                  <div className="htl-venue-name">{v.name}</div>
                  <div className="htl-venue-line">{v.line}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why it's different ── */}
      <section className="htl-values">
        {VALUES.map((v) => (
          <div className="htl-value" key={v.title}>
            <div className="htl-value-title">{v.title}</div>
            <div className="htl-value-body">{v.body}</div>
          </div>
        ))}
      </section>

      {/* ── How it works + closing CTA ── */}
      <section className="htl-how">
        <div className="htl-steps">
          {STEPS.map((s) => (
            <div className="htl-step" key={s.n}>
              <span className="htl-step-n">{s.n}</span>
              <div className="htl-step-title">{s.title}</div>
              <div className="htl-step-body">{s.body}</div>
            </div>
          ))}
        </div>
        {enterButton("Walk in — the radio's on →")}
      </section>

      <style>{`
        .htl-root {
          position: fixed; inset: 0; z-index: 100;
          overflow-y: auto; overflow-x: hidden;
          background: #0a0a0f; color: #e8eaf0;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          transition: opacity 0.85s ease;
          -webkit-overflow-scrolling: touch;
        }
        .htl-root ::selection { background: #f5a623; color: #1a1205; }
        .htl-root a { text-decoration: none; }

        /* nav */
        .htl-nav {
          position: sticky; top: 0; z-index: 5;
          display: flex; align-items: center; gap: 16px;
          padding: 12px 22px; background: rgba(10,10,15,0.72);
          backdrop-filter: blur(8px); border-bottom: 1px solid rgba(255,255,255,0.08);
        }
        .htl-nav-logo { height: 26px; width: auto; }
        .htl-nav-signin {
          margin-left: auto; font-size: 13px; color: #c7cfe0; cursor: pointer;
          background: none; border: 1px solid rgba(255,255,255,0.18); border-radius: 9px; padding: 7px 15px;
        }
        .htl-nav-signin:hover { border-color: rgba(245,166,35,0.5); color: #ffd27a; }

        /* buttons */
        .htl-btn {
          display: inline-flex; align-items: center; justify-content: center; gap: 9px;
          font-size: 16px; font-weight: 700; padding: 15px 28px; border-radius: 12px;
          border: 1px solid transparent; cursor: pointer; font-family: inherit;
          transition: transform 0.15s ease, box-shadow 0.25s ease, background 0.25s ease;
        }
        .htl-btn--primary {
          background: linear-gradient(135deg, #ff7b00 0%, #f5a623 50%, #1e88e5 100%);
          color: #fff; box-shadow: 0 8px 32px rgba(245,158,11,0.35);
        }
        .htl-btn--primary:hover { transform: translateY(-1px); }
        .htl-btn--ghost {
          background: rgba(7,10,18,0.55); border-color: rgba(245,166,35,0.4);
          color: #f5d9a0; backdrop-filter: blur(4px);
        }
        .htl-btn--ghost:hover { background: rgba(58,47,24,0.55); }
        .htl-btn.is-loading { opacity: 0.85; cursor: default; }
        .htl-btn.is-loading:hover { transform: none; }
        .htl-loading { display: inline-flex; align-items: center; gap: 10px; }
        .htl-spinner {
          width: 15px; height: 15px; border-radius: 50%;
          border: 2px solid rgba(255,233,196,0.3); border-top-color: #ffb25e;
          display: inline-block; animation: htl-spin 0.8s linear infinite;
        }
        @keyframes htl-spin { to { transform: rotate(360deg); } }

        /* hero */
        .htl-hero {
          position: relative; min-height: 620px; display: flex; align-items: center;
          justify-content: center; overflow: hidden;
          border-bottom: 1px solid rgba(255,255,255,0.1);
        }
        .htl-hero-video { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
        .htl-hero-scrim {
          position: absolute; inset: 0; pointer-events: none;
          background:
            radial-gradient(90% 100% at 50% 100%, rgba(58,32,8,0.55), transparent 60%),
            linear-gradient(180deg, rgba(10,10,15,0.55) 0%, rgba(10,10,15,0.15) 40%, rgba(10,10,15,0.72) 100%);
        }
        .htl-hero-inner {
          position: relative; z-index: 2; display: flex; flex-direction: column;
          align-items: center; text-align: center; padding: 72px 24px; max-width: 760px;
        }
        .htl-badge {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(7,10,18,0.68); border: 1px solid rgba(245,166,35,0.35);
          border-radius: 16px; padding: 9px 16px; font: 11px/1 ui-monospace, monospace;
          letter-spacing: 0.28em; color: #c9b98a;
        }
        .htl-badge-dot { width: 6px; height: 6px; border-radius: 50%; background: #f5a623; animation: htl-pulse 1.6s infinite; }
        @keyframes htl-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }
        .htl-title {
          font-weight: 800; font-size: clamp(40px, 8vw, 68px); line-height: 1.03;
          letter-spacing: -0.025em; margin: 22px 0 0; text-shadow: 0 2px 30px rgba(0,0,0,0.7);
        }
        .htl-sub {
          font-size: 18px; line-height: 1.55; color: #d8dce8; max-width: 640px;
          margin: 18px 0 0; text-shadow: 0 1px 12px rgba(0,0,0,0.8);
        }
        .htl-cta { display: flex; gap: 14px; margin-top: 30px; flex-wrap: wrap; justify-content: center; }
        .htl-fineprint { font-size: 13px; color: #a8b0c4; margin-top: 16px; text-shadow: 0 1px 8px rgba(0,0,0,0.8); }
        .htl-hint { font-size: 12px; letter-spacing: 0.06em; color: rgba(255,178,94,0.72); margin-top: 8px; min-height: 15px; }

        /* login form */
        .htl-login { display: flex; flex-direction: column; gap: 10px; width: min(320px, 82vw); margin-top: 28px; }
        .htl-input {
          width: 100%; padding: 13px 14px; border-radius: 12px; box-sizing: border-box;
          border: 1px solid rgba(255,178,94,0.35); background: rgba(255,255,255,0.06);
          color: #ffe9c4; font-size: 16px; font-family: inherit; outline: none;
        }
        .htl-autherr { color: #ff8a9c; font-size: 13px; }
        .htl-linkbtn { background: none; border: none; color: rgba(255,210,125,0.75); font: 600 13px system-ui; cursor: pointer; padding: 6px; }

        /* shared section label */
        .htl-section-label { font: 11px/1 ui-monospace, monospace; letter-spacing: 0.28em; text-transform: uppercase; color: #c9b98a; margin-bottom: 18px; }

        /* intro */
        .htl-intro { text-align: center; padding: 44px 24px 8px; }
        .htl-intro p { font-size: 18px; line-height: 1.65; color: #c7cfe0; max-width: 700px; margin: 0 auto; }

        /* venues marquee — the cards scroll; hovering the row parks them so one
           can be read (and the images are big enough to carry the "scenes" job
           the stills row used to do). */
        .htl-venues { max-width: 1280px; margin: 0 auto; padding: 40px 24px; }
        .htl-marquee-viewport {
          overflow: hidden; padding-bottom: 6px;
          -webkit-mask-image: linear-gradient(90deg, transparent, #000 5%, #000 95%, transparent);
          mask-image: linear-gradient(90deg, transparent, #000 5%, #000 95%, transparent);
        }
        .htl-marquee-track { display: flex; width: max-content; animation: htl-marquee 44s linear infinite; }
        .htl-marquee-track:hover { animation-play-state: paused; }
        @keyframes htl-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .htl-venue {
          flex: 0 0 320px; margin-right: 16px; display: flex; flex-direction: column;
          background: #0f1524; border: 1px solid rgba(255,255,255,0.1); border-radius: 14px; overflow: hidden;
          transition: border-color 0.2s ease, transform 0.2s ease;
        }
        .htl-venue:hover { border-color: rgba(245,166,35,0.4); transform: translateY(-2px); }
        .htl-venue-media { position: relative; height: 170px; overflow: hidden; }
        .htl-venue-media img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
        .htl-venue-body { padding: 15px 15px 18px; }
        .htl-venue-emoji { font-size: 20px; }
        .htl-venue-name { font-weight: 700; font-size: 15px; margin-top: 7px; }
        .htl-venue-line { font-size: 13px; line-height: 1.55; color: #8b94a8; margin-top: 6px; }

        /* values */
        .htl-values { max-width: 1280px; margin: 0 auto; padding: 8px 24px 44px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
        .htl-value { background: linear-gradient(160deg, #1a1205, #0f1524); border: 1px solid rgba(245,166,35,0.28); border-radius: 14px; padding: 24px; }
        .htl-value-title { font-weight: 800; font-size: 22px; letter-spacing: -0.02em; color: #f5b94a; }
        .htl-value-body { font-size: 14px; line-height: 1.55; color: #c7cfe0; margin-top: 9px; }

        /* how it works */
        .htl-how {
          border-top: 1px solid rgba(255,255,255,0.08); max-width: 1280px; margin: 0 auto;
          padding: 36px 24px 56px; display: flex; align-items: center; gap: 40px; flex-wrap: wrap;
        }
        .htl-steps { display: flex; gap: 34px; flex: 1; flex-wrap: wrap; }
        .htl-step-n { font: 12px/1 ui-monospace, monospace; color: #f5b94a; }
        .htl-step-title { font-weight: 700; font-size: 15px; margin-top: 7px; }
        .htl-step-body { font-size: 13px; color: #8b94a8; margin-top: 4px; }

        @media (max-width: 860px) {
          .htl-values { grid-template-columns: 1fr; }
        }
        @media (max-width: 560px) {
          .htl-venue { flex-basis: 268px; margin-right: 12px; }
          .htl-venue-media { height: 140px; }
          .htl-how { flex-direction: column; align-items: flex-start; }
        }
        @media (prefers-reduced-motion: reduce) {
          .htl-marquee-track { animation: none; }
          .htl-marquee-viewport { overflow-x: auto; }
          .htl-badge-dot { animation: none; }
        }
      `}</style>
    </div>
  );
}
